import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Badge,
  Group,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconArrowRight,
  IconCloudUpload,
  IconLink,
  IconExternalLink,
  IconPencil,
} from '@tabler/icons-react';

export default function AviaryV2ArticleNewPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const defaultRole = location.state?.defaultRole || 'promo';
  const isKb = defaultRole === 'context';

  return (
    <Box style={{ padding: '32px 24px 120px', maxWidth: 860, margin: '0 auto' }}>
      <Stack gap="lg">
        <UnstyledButton
          onClick={() => navigate(isKb ? '/v2/knowledge-base' : '/v2/articles')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--aviary-muted)',
            fontSize: 13,
            width: 'fit-content',
          }}
        >
          <IconArrowLeft size={14} /> {isKb ? 'Knowledge Base' : 'Articles'}
        </UnstyledButton>

        <Stack gap={4}>
          <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28 }}>
            {isKb ? 'Add to Knowledge Base' : 'Add an article'}
          </Text>
          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
            {isKb
              ? 'Write the content your agent uses to understand your business, voice, and rules.'
              : 'However it&apos;s made, the Aviary mints a magic link per person and tracks every open. Pick how you want to start.'}
          </Text>
        </Stack>

        <Stack gap="sm">
          <MethodTile
            icon={IconPencil}
            title="Co-write with Aviary AI"
            description="Type a topic and optional notes, then get a full first draft you can edit and publish."
            onClick={() => navigate('/v2/articles/new/cocreate', { state: { back: { path: '/v2/articles/new', label: 'Add' }, defaultRole } })}
          />

          <MethodTile
            icon={IconExternalLink}
            title="External URL"
            description="Point Aviary at an article you host elsewhere (WordPress, Substack, etc.) and track engagement."
            onClick={() => navigate('/v2/articles/new/external', { state: { back: { path: '/v2/articles/new', label: 'Add' }, defaultRole } })}
          />

          <MethodTile
            icon={IconLink}
            title="Add a link"
            description="Point to an article you&apos;ve already published anywhere. We mint magic links straight to that URL."
            subDescription="Tracks opens per person. Deep engagement (dwell, reactions) only works on Aviary-hosted articles."
            onClick={() => navigate('/v2/articles/new/link', { state: { back: { path: '/v2/articles/new', label: 'Add' }, defaultRole } })}
          />

          <MethodTile
            icon={IconCloudUpload}
            title="Upload an HTML file"
            description="Already wrote it elsewhere? Drop the file and we host it."
            onClick={() => navigate('/v2/articles/new/upload', { state: { back: { path: '/v2/articles/new', label: 'Add' }, defaultRole } })}
          />
        </Stack>

        <Text size="xs" style={{ color: 'var(--mantine-color-sage-8)', fontStyle: 'italic' }}>
          nothing is populated yet - each path opens its own screen
        </Text>
      </Stack>
    </Box>
  );
}

function MethodTile({
  icon: Icon,
  title,
  description,
  subDescription = '',
  onClick,
  disabled = false,
  badge = null,
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{ display: 'block', width: '100%' }}
      disabled={disabled}
    >
      <Paper
        p="md"
        radius="md"
        withBorder
        style={{
          background: '#fff',
          opacity: disabled ? 0.8 : 1,
          transition: 'border-color 120ms ease, box-shadow 120ms ease',
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="sm" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
            <Box
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: 'var(--mantine-color-sage-0)',
                border: '1px solid var(--mantine-color-sage-2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon size={18} style={{ color: 'var(--mantine-color-sage-7)' }} />
            </Box>
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Group gap={8} align="center">
                <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 16 }}>{title}</Text>
                {badge && (
                  <Badge size="xs" color="gray" variant="light">
                    {badge}
                  </Badge>
                )}
              </Group>
              <Text size="sm" style={{ color: 'var(--aviary-muted)', lineHeight: 1.4 }}>
                {description}
              </Text>
              {subDescription && (
                <Text size="xs" c="dimmed" style={{ lineHeight: 1.35 }}>
                  {subDescription}
                </Text>
              )}
            </Stack>
          </Group>
          {!disabled && <IconArrowRight size={16} style={{ color: 'var(--aviary-muted)', flexShrink: 0 }} />}
        </Group>
      </Paper>
    </UnstyledButton>
  );
}
